import { useState, useCallback, useEffect, useRef } from 'react';
import { draftService } from '../../services';
import { Draft, CreateDraftDTO, UpdateDraftDTO } from '../../models';
import { createLogger } from '../../services/Debug';
export function useDraftManager() {
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const log = createLogger('useDraftManager');
  // Track the last known modified date for conflict detection
  const lastModifiedRef = useRef(new Map());

  // ===== Local storage cache =====
  const STORAGE_KEY = 'dictaphone.drafts.cache.v1';

  const serializeDraftsForCache = useCallback((draftArray) => {
    try {
      const plain = (draftArray || []).map((d) => ({
        id: d.id,
        status: d.status,
        report_type: d.report_type,
        content: d.content,
        annex: d.annex,
        date_created: d.date_created instanceof Date ? d.date_created.toISOString() : d.date_created,
        date_modified: d.date_modified instanceof Date ? d.date_modified.toISOString() : d.date_modified
      }));
      return JSON.stringify({ v: 1, drafts: plain });
    } catch (e) {
      return null;
    }
  }, []);

  const saveDraftsToCache = useCallback((draftArray) => {
    try {
      const payload = serializeDraftsForCache(draftArray);
      if (payload) localStorage.setItem(STORAGE_KEY, payload);
    } catch (_) {}
  }, [serializeDraftsForCache]);

  const loadDraftsFromCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const arr = Array.isArray(parsed?.drafts) ? parsed.drafts : [];
      const objects = arr.map((d) => new Draft(d));
      return objects;
    } catch (e) {
      return [];
    }
  }, []);

  const hydrateFromCache = useCallback(() => {
    const cached = loadDraftsFromCache();
    if (cached && cached.length > 0) {
      // Update last modified map as well
      lastModifiedRef.current.clear();
      cached.forEach((d) => lastModifiedRef.current.set(d.id, d.date_modified));
      setDrafts(cached);
    }
  }, [loadDraftsFromCache]);

  // Load all drafts from API
  const loadDrafts = useCallback(async () => {
    try {
      // Optimistic show: hydrate cache first (show -> loading -> show)
      if (drafts.length === 0) {
        hydrateFromCache();
      }
      setIsLoadingDrafts(true);
      setDraftError(null);
      
      const response = await draftService.getAllDrafts(true); // Include deleted drafts
      // Handle the response structure { success: true, data: [...] }
      log.debug('Drafts response:', response);
      const draftsArray = response.data || response;
      const draftObjects = draftsArray.map(d => new Draft(d));
      
      // Update our last modified tracking
      lastModifiedRef.current.clear();
      draftObjects.forEach(draft => {
        lastModifiedRef.current.set(draft.id, draft.date_modified);
      });
      
      setDrafts(draftObjects);
      // Persist fresh data to cache
      saveDraftsToCache(draftObjects);
    } catch (error) {
      log.error('Failed to load drafts:', error);
      setDraftError(error.message);
      // Keep existing state (possibly from cache) on error
    } finally {
      setIsLoadingDrafts(false);
    }
  }, [drafts.length, hydrateFromCache, saveDraftsToCache]);

  // Create a new draft
  const createDraft = useCallback(async (reportType, text, annex, status) => {
    try {
      const createDto = new CreateDraftDTO({
        report_type: reportType || 'texte',
        content: text || '',
        annex: annex || null,
        status: status || 'draft'
      });
      
      const response = await draftService.createDraft(createDto);
      // Handle the response structure if it has a data property
      const draftData = response.data || response;
      const newDraft = new Draft(draftData);
      
      // Update local state
      setDrafts(prevDrafts => {
        const next = [...prevDrafts, newDraft];
        // Persist cache
        saveDraftsToCache(next);
        return next;
      });
      lastModifiedRef.current.set(newDraft.id, newDraft.date_modified);
      setCurrentDraftId(newDraft.id);
      
      return newDraft;
    } catch (error) {
      log.error('Failed to create draft:', error);
      throw error;
    }
  }, []);

  // Update an existing draft with conflict detection
  const updateDraft = useCallback(async (draftId, updates) => {
    try {
      // Check for conflicts
      const lastKnownModified = lastModifiedRef.current.get(draftId);
      const currentDraft = drafts.find(d => d.id === draftId);
      
      if (currentDraft && lastKnownModified) {
        // Fetch latest version to check for conflicts
        const latestResponse = await draftService.getDraftById(draftId);
        const latestDraft = latestResponse.data || latestResponse;
        const latestModified = new Date(latestDraft.date_modified);
        
        if (latestModified > lastKnownModified) {
          log.warn('Draft conflict detected - server version is newer');
          // You could implement a conflict resolution UI here
          // For now, we'll proceed with the update but log the conflict
        }
      }
      
      const updateDto = new UpdateDraftDTO(updates);
      const response = await draftService.updateDraft(draftId, updateDto);
      const draftData = response.data || response;
      const updatedDraft = new Draft(draftData);
      
      // Update local state
      setDrafts(prevDrafts => {
        const next = prevDrafts.map(d => d.id === draftId ? updatedDraft : d);
        saveDraftsToCache(next);
        return next;
      });
      lastModifiedRef.current.set(updatedDraft.id, updatedDraft.date_modified);
      
      return updatedDraft;
    } catch (error) {
      log.error('Failed to update draft:', error);
      throw error;
    }
  }, [drafts]);

  // Save or update draft (used when sending report or deleting)
  const saveOrUpdateDraft = useCallback(async (transcript, annexText, selectedReportType, status) => {
    const content = String(transcript || '').trim();
    const annex = String(annexText || '').trim();
    const reportType = String(selectedReportType || 'texte');
    const draftStatus = String(status || 'draft');

    if (!content && !annex) {
      return null;
    }

    try {
      if (currentDraftId) {
        // Update existing draft
        return await updateDraft(currentDraftId, {
          report_type: reportType,
          content: content,
          annex: annex || null,
          status: draftStatus
        });
      } else {
        // Create new draft
        return await createDraft(reportType, content, annex, draftStatus);
      }
    } catch (error) {
      log.error('Failed to save draft:', error);
      // Don't throw - we don't want to block the UI
      return null;
    }
  }, [currentDraftId, updateDraft, createDraft]);

  // Move draft to sent status
  const moveCurrentDraftToSent = useCallback(async (text, annex) => {
    if (!currentDraftId) return;
    
    try {
      await updateDraft(currentDraftId, {
        content: text,
        annex: annex || null,
        status: 'sent'
      });
      setCurrentDraftId(null);
    } catch (error) {
      log.error('Failed to move draft to sent:', error);
    }
  }, [currentDraftId, updateDraft]);

  // Move draft to trash
  const handleMoveToTrash = useCallback(async () => {
    if (!currentDraftId) return false;
    
    try {
      setCurrentDraftId(null);
      return true;
    } catch (error) {
      log.error('Failed to move draft to trash:', error);
      return false;
    }
  }, [currentDraftId, updateDraft]);

  // Delete draft (soft or permanent)
  const handleDeleteDraft = useCallback(async (draftId, permanent = false) => {
    try {
      await draftService.deleteDraft(draftId, permanent);
      
      if (permanent) {
        // Remove from local state
        setDrafts(prevDrafts => {
          const next = prevDrafts.filter(d => d.id !== draftId);
          saveDraftsToCache(next);
          return next;
        });
        lastModifiedRef.current.delete(draftId);
      } else {
        // Update status to deleted
        setDrafts(prevDrafts => {
          const next = prevDrafts.map(d => d.id === draftId ? { ...d, status: 'deleted' } : d);
          saveDraftsToCache(next);
          return next;
        });
      }
      
      if (draftId === currentDraftId) {
        setCurrentDraftId(null);
      }
    } catch (error) {
      log.error('Failed to delete draft:', error);
      throw error;
    }
  }, [currentDraftId]);

  // Delete multiple drafts
  const handleDeleteAllDrafts = useCallback(async (draftIds, permanent = false) => {
    try {
      // Delete all drafts in parallel
      await Promise.all(
        draftIds.map(id => draftService.deleteDraft(id, permanent))
      );
      
      if (permanent) {
        // Remove from local state
        setDrafts(prevDrafts => {
          const next = prevDrafts.filter(d => !draftIds.includes(d.id));
          saveDraftsToCache(next);
          return next;
        });
        draftIds.forEach(id => lastModifiedRef.current.delete(id));
      } else {
        // Update status to deleted
        setDrafts(prevDrafts => {
          const next = prevDrafts.map(d => 
            draftIds.includes(d.id) ? { ...d, status: 'deleted' } : d
          );
          saveDraftsToCache(next);
          return next;
        });
      }
      
      if (draftIds.includes(currentDraftId)) {
        setCurrentDraftId(null);
      }
    } catch (error) {
      log.error('Failed to delete drafts:', error);
      throw error;
    }
  }, [currentDraftId]);

  // Bulk clear a whole section using API bulk endpoints when available
  const handleBulkClearSection = useCallback(async (section) => {
    try {
      if (section === 'active') {
        await draftService.bulkDeleteDraft();
        setDrafts(prevDrafts => {
          const next = prevDrafts.filter(d => d.status !== 'draft');
          saveDraftsToCache(next);
          return next;
        });
        if (currentDraftId) {
          const current = drafts.find(d => d.id === currentDraftId);
          if (current && current.status === 'draft') {
            setCurrentDraftId(null);
          }
        }
      } else if (section === 'sent') {
        await draftService.bulkDeleteSent();
        setDrafts(prevDrafts => {
          const next = prevDrafts.filter(d => d.status !== 'sent');
          saveDraftsToCache(next);
          return next;
        });
        if (currentDraftId) {
          const current = drafts.find(d => d.id === currentDraftId);
          if (current && current.status === 'sent') {
            setCurrentDraftId(null);
          }
        }
      } else if (section === 'deleted') {
        try {
          await draftService.bulkDeletePermanent();
          setDrafts(prevDrafts => {
            const next = prevDrafts.filter(d => d.status !== 'deleted');
            saveDraftsToCache(next);
            return next;
          });
          lastModifiedRef.current = new Map(
            Array.from(lastModifiedRef.current.entries()).filter(([id]) =>
              drafts.find(d => d.id === id && d.status !== 'deleted')
            )
          );
          if (currentDraftId) {
            const current = drafts.find(d => d.id === currentDraftId);
            if (current && current.status === 'deleted') {
              setCurrentDraftId(null);
            }
          }
        } catch (e) {
          // Fallback: permanently delete all items in trash one by one
          const ids = drafts.filter(d => d.status === 'deleted').map(d => d.id);
          if (ids.length > 0) {
            await Promise.all(ids.map(id => draftService.deleteDraft(id, true)));
            setDrafts(prevDrafts => {
              const next = prevDrafts.filter(d => d.status !== 'deleted');
              saveDraftsToCache(next);
              return next;
            });
            ids.forEach(id => lastModifiedRef.current.delete(id));
            if (ids.includes(currentDraftId)) {
              setCurrentDraftId(null);
            }
          }
        }
      } else if (section === 'live') {
        try {
          await draftService.bulkDeleteLive();
          setDrafts(prevDrafts => {
            const next = prevDrafts.filter(d => d.status !== 'live');
            saveDraftsToCache(next);
            return next;
          });
          if (currentDraftId) {
            const current = drafts.find(d => d.id === currentDraftId);
            if (current && current.status === 'live') {
              setCurrentDraftId(null);
            }
          }
        } catch (e) {
          // Fallback: delete all live items one by one
          const ids = drafts.filter(d => d.status === 'live').map(d => d.id);
          if (ids.length > 0) {
            await Promise.all(ids.map(id => draftService.deleteDraft(id, true)));
            setDrafts(prevDrafts => {
              const next = prevDrafts.filter(d => d.status !== 'live');
              saveDraftsToCache(next);
              return next;
            });
            ids.forEach(id => lastModifiedRef.current.delete(id));
            if (ids.includes(currentDraftId)) {
              setCurrentDraftId(null);
            }
          }
        }
      }
    } catch (error) {
      log.error('Failed to bulk clear drafts:', error);
      throw error;
    }
  }, [drafts, currentDraftId, saveDraftsToCache]);

  // Restore deleted draft
  const handleRestoreDeleted = useCallback(async (draftId) => {
    try {
      await updateDraft(draftId, {
        status: 'draft'
      });
    } catch (error) {
      log.error('Failed to restore draft:', error);
      throw error;
    }
  }, [updateDraft]);

  // On mount, hydrate from cache to show immediately
  useEffect(() => {
    hydrateFromCache();
  }, [hydrateFromCache]);

  // Select a draft
  const handleSelectDraft = useCallback((draft, actionMode = 'replaceAll') => {
    setCurrentDraftId(draft.id);
    
    // Update last known modified date
    lastModifiedRef.current.set(draft.id, draft.date_modified);
    
    // If loading full draft and it's deleted, restore it
    if (actionMode === 'replaceAll' && draft.status === 'deleted') {
      
      handleRestoreDeleted(draft.id);
    }
    
    return { draft, actionMode };
  }, [handleRestoreDeleted]);

  // Filter drafts by status
  const activeDrafts = drafts.filter(d => d.status === 'draft');
  const sentDrafts = drafts.filter(d => d.status === 'sent');
  const deletedDrafts = drafts.filter(d => d.status === 'deleted');
  const liveDrafts = drafts.filter(d => d.status === 'live');

  return {
    // State
    drafts,
    currentDraftId,
    activeDrafts,
    sentDrafts,
    deletedDrafts,
    liveDrafts,
    isLoadingDrafts,
    draftError,
    
    // Actions
    loadDrafts,
    saveOrUpdateDraft,
    handleSelectDraft,
    handleDeleteDraft,
    handleDeleteAllDrafts,
    handleRestoreDeleted,
    moveCurrentDraftToSent,
    handleMoveToTrash,
    setCurrentDraftId,
    handleBulkClearSection
  };
}